import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, forkJoin, from, map, of, take, tap, firstValueFrom } from 'rxjs';
import { DbService } from './db.service';
import { DataSynchronizerService } from './data-synchronization.service';
import { AppDb, DbKsefInvoice } from 'fakturnia-shared';
import { DateTime } from 'luxon';
import { HttpClient } from '@angular/common/http';
import { API_HOST } from 'environments/environment';

@Injectable({
    providedIn: 'root'
})
export class KsefInvoicesService implements OnDestroy {

    private _subscriptions: Subscription[] = [];

    private _invoices = new BehaviorSubject<DbKsefInvoiceObject>({});
    currentInvoices = this._invoices.asObservable();

    private _db: AppDb;

    ngOnDestroy(): void {
        this._subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Czyści cache w pamięci (np. przy wylogowaniu / przełączeniu konta).
     */
    public clear(): void {
        this._invoices.next({});
    }

    constructor(
        private _dbService: DbService,
        private _dataSynchronizerService: DataSynchronizerService,
        private _http: HttpClient
    ) {
        this._subscriptions.push(this._dbService.getDatabase().subscribe({
            next: (data) => {
                // Reset cache za każdym razem gdy baza się zmienia (logout/switch konta).
                this._db = data;
                this._invoices.next({});
                if (data) {
                    this.getAll().pipe(take(1)).subscribe();
                }
            }
        }));

        this._subscriptions.push(this._dataSynchronizerService.currentData.subscribe({
            next: (data) => {
                if (!data) return;
                if (data.table != 'ksefInvoices') return;

                // Update
                data.value.forEach((invoice: DbKsefInvoice) => {
                    this._invoices.value[invoice.ksefNumber] = invoice;
                });

                // Push changes
                this._invoices.next(this._invoices.value);

                this._dataSynchronizerService.received();
            }
        }));
    }

    /**
     * Pobiera fakturę po KSeF number
     */
    getByKsefNumber(ksefNumber: string): DbKsefInvoice | null {
        const invoice = this._invoices.value[ksefNumber];

        if (!invoice) {
            console.warn(`KSeF Invoice with number: ${ksefNumber} not found.`);
            return null;
        }
        return invoice;
    }

    /**
     * Pobiera faktury po lokalnym ID dokumentu
     */
    getByLocalDocumentId(localDocumentId: string): DbKsefInvoice[] {
        return Object.values(this._invoices.value)
            .filter((invoice: DbKsefInvoice) => invoice.localDocumentId === localDocumentId && !invoice.isDeleted);
    }

    /**
     * Ładuje wszystkie faktury z Dexie do pamięci
     */
    getAll(): Observable<any> {
        if (!this._db) return of(null);
        
        const s1 = new Date().getTime();
        const invoiceLoader = this._db.transaction("r", this._db.ksefInvoices, () => {
            return this._db.ksefInvoices.orderBy('acquisitionDate').reverse().toArray();
        }).then((result: DbKsefInvoice[]) => {
            const object: DbKsefInvoiceObject = {};
            result.forEach(invoice => {
                object[invoice.ksefNumber] = invoice;
            });
            return object;
        });

        return forkJoin([from(invoiceLoader)]).pipe(
            map(([invoices]) => {
                // Update subject
                this._invoices.next(invoices);
                return { invoices };
            }),
            tap(() => console.log(`[KSeF Invoices Service]: Built in ${new Date().getTime() - s1}ms.`))
        );
    }

    /**
     * Formatuje datę do polskiego formatu
     */
    formatDate(date: Date | string): string {
        try {
            const dt = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
            return dt.toFormat('dd.MM.yyyy');
        } catch {
            return '-';
        }
    }

    /**
     * Formatuje kwotę z walutą
     */
    formatAmount(amount: number, currency: string): string {
        return `${amount.toFixed(2)} ${currency}`;
    }

    /**
     * Zwraca label dla typu faktury (formCode.value)
     */
    getFormCodeLabel(formCode: string | { value: string }): string {
        const codeValue = typeof formCode === 'string' ? formCode : formCode.value;
        const types: { [key: string]: string } = {
            'FA': 'Faktura VAT',
            'FAP': 'Faktura Proforma',
            'RO': 'Rachunek',
            'WEW': 'Faktura wewnętrzna',
            'FP': 'Faktura Zaliczkowa',
            'PEF': 'Faktura PEF'
        };
        return types[codeValue] || codeValue;
    }

    /**
     * Zwraca dane nabywcy z faktury KSeF
     * Określa na podstawie seller.nip czy to faktura wystawiona czy otrzymana
     */
    getBuyerInfo(invoice: DbKsefInvoice): { name: string; identifier: string } {
        // Jeśli seller.nip nie istnieje, użyj buyer
        const buyerName = invoice.buyer?.name || '-';
        const buyerIdentifier = invoice.buyer?.identifier?.value || invoice.buyer?.nip || '-';
        
        return {
            name: buyerName,
            identifier: buyerIdentifier
        };
    }

    /**
     * Zwraca dane sprzedawcy z faktury KSeF
     */
    getSellerInfo(invoice: DbKsefInvoice): { name: string; identifier: string } {
        const sellerName = invoice.seller?.name || '-';
        const sellerIdentifier = invoice.seller?.nip || '-';
        
        return {
            name: sellerName,
            identifier: sellerIdentifier
        };
    }

    /**
     * Pobiera XML faktury
     * 1. Sprawdza lokalnie w Dexie (cache)
     * 2. Jeśli brak, pobiera z backendu (który pobiera z KSeF i zapisuje w MongoDB)
     * 3. Przy następnej synchronizacji frontend dostanie XML automatycznie
     */
    async getInvoiceXml(ksefNumber: string): Promise<string | null> {
        try {
            // 1. Sprawdź lokalnie w Dexie
            const localInvoice = await this._db.ksefInvoices.get(ksefNumber);
            if (localInvoice?.xmlContent) {
                console.log(`[KSeF] XML loaded from Dexie cache for ${ksefNumber}`);
                return localInvoice.xmlContent;
            }

            // 2. Pobierz z backendu (który pobierze z KSeF i zapisze w MongoDB)
            console.log(`[KSeF] Fetching XML from backend for ${ksefNumber}`);
            const response: any = await firstValueFrom(
                this._http.get(`${API_HOST}ksef/invoices/${ksefNumber}`)
            );

            if (response.success && response.data) {
                // 3. Zapisz w Dexie (opcjonalnie, przy następnej synchronizacji też się pojawi)
                await this._db.ksefInvoices.update(ksefNumber, {
                    xmlContent: response.data
                });
                console.log(`[KSeF] XML saved to Dexie for ${ksefNumber}`);
                return response.data
            }

            return null;
        } catch (error) {
            console.error(`[KSeF] Error fetching XML for ${ksefNumber}:`, error);
            return null;
        }
    }

    /**
     * Pobiera UPO faktury
     * 1. Sprawdza lokalnie w Dexie (cache)
     * 2. Jeśli brak, pobiera z backendu (który pobiera z KSeF i zapisuje w MongoDB)
     * 3. Przy następnej synchronizacji frontend dostanie UPO automatycznie
     */
    async getInvoiceUpo(ksefNumber: string): Promise<string | null> {
        try {
            // 1. Sprawdź lokalnie w Dexie
            const localInvoice = await this._db.ksefInvoices.get(ksefNumber);
            if (localInvoice?.upo) {
                console.log(`[KSeF] UPO loaded from Dexie cache for ${ksefNumber}`);
                return localInvoice.upo;
            }

            // 2. Pobierz z backendu (który pobierze z KSeF i zapisze w MongoDB)
            console.log(`[KSeF] Fetching UPO from backend for ${ksefNumber}`);
            const response: any = await firstValueFrom(
                this._http.get(`${API_HOST}ksef/invoices/${ksefNumber}/upo`)
            );

            if (response.success && response.data?.upo) {
                // 3. Zapisz w Dexie (opcjonalnie, przy następnej synchronizacji też się pojawi)
                await this._db.ksefInvoices.update(ksefNumber, {
                    upo: response.data.upo
                });
                console.log(`[KSeF] UPO saved to Dexie for ${ksefNumber}`);
                return response.data.upo;
            }

            return null;
        } catch (error) {
            console.error(`[KSeF] Error fetching UPO for ${ksefNumber}:`, error);
            return null;
        }
    }
}

export interface DbKsefInvoiceObject {
    [key: string]: DbKsefInvoice;
}
